import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import ClickToCall from "./ClickToCall";
import { LeadEmail } from "./LeadEmail";
import { STATUSES, statusMeta, sourceIcon, fmtMoney, fmtDate } from "../lib/constants";
import {
  Phone,
  MessageSquare,
  StickyNote,
  ArrowRightLeft,
  Send,
  Trash2,
  CalendarClock,
  PlusCircle,
  Repeat,
  MapPin,
  Globe,
} from "lucide-react";

const activityIcon = { call: Phone, sms: MessageSquare, note: StickyNote, status_change: ArrowRightLeft, follow_up: CalendarClock, created: PlusCircle };

export default function LeadDrawer({ leadId, open, onOpenChange, defaultTab = "timeline" }) {
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [sms, setSms] = useState("");
  const [note, setNote] = useState("");
  const [fuDate, setFuDate] = useState("");
  const [fuMsg, setFuMsg] = useState("");
  const [team, setTeam] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [enriching, setEnriching] = useState(false);
  const qc = useQueryClient();

  const load = useCallback(async () => {
    if (!leadId) return;
    try {
      const [l, a, m, en] = await Promise.all([
        api.get(`/leads/${leadId}`),
        api.get(`/leads/${leadId}/activities`),
        api.get(`/leads/${leadId}/messages`),
        api.get(`/enrollments`, { params: { lead_id: leadId } }),
      ]);
      setLead(l.data);
      setActivities(a.data);
      setMessages(m.data);
      setEnrollments(en.data);
    } catch (e) {
      toast.error("Couldn't load this lead's details. Please try again.");
    }
  }, [leadId]);

  useEffect(() => {
    if (open && leadId) {
      load();
      api.get("/sms/templates").then((r) => setTemplates(r.data)).catch(() => {});
      api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
      api.get("/sequences").then((r) => setSequences(r.data)).catch(() => {});
    }
  }, [open, leadId, load]);

  const refresh = () => { load(); qc.invalidateQueries(); };

  const changeStatus = async (status) => {
    await api.patch(`/leads/${leadId}/status`, { status });
    toast.success("Status updated");
    refresh();
  };

  const sendSms = async () => {
    if (!sms.trim()) return;
    try {
      await api.post("/sms/send", { lead_id: leadId, body: sms });
      toast.success("SMS sent");
      setSms("");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/leads/${leadId}/notes`, { body: note });
    setNote("");
    toast.success("Note added");
    refresh();
  };

  const scheduleFollowup = async () => {
    if (!fuDate) { toast.error("Pick a date/time"); return; }
    try {
      await api.post("/followups", {
        lead_id: leadId,
        scheduled_at: new Date(fuDate).toISOString(),
        channel: "sms",
        message: fuMsg,
        auto: true,
      });
      toast.success("Follow-up scheduled");
      setFuDate(""); setFuMsg("");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async () => {
    await api.delete(`/leads/${leadId}`);
    toast.success("Lead deleted");
    qc.invalidateQueries();
    onOpenChange(false);
  };

  const assign = async (value) => {
    await api.patch(`/leads/${leadId}/assign`, { assigned_to: value });
    toast.success(value ? `Assigned to ${value}` : "Unassigned");
    refresh();
  };

  const enroll = async (sequenceId) => {
    try {
      await api.post("/sequences/enroll", { lead_id: leadId, sequence_id: sequenceId });
      toast.success("Enrolled in drip");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const stopEnroll = async (id) => {
    await api.post(`/enrollments/${id}/stop`);
    toast.success("Drip stopped");
    refresh();
  };

  const aiCall = async () => {
    try {
      await api.post("/ai/call", { lead_id: leadId, queue: true });
      toast.success("Added to the AI Qualify queue — start the call from the AI Agent page");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const enrichLead = async () => {
    setEnriching(true);
    try {
      const r = await api.post(`/leads/${leadId}/enrich`, {});
      toast.success(`Enriched from Google: ${r.data.match?.name || "match found"}`);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setEnriching(false);
    }
  };

  if (!lead) return <Sheet open={open} onOpenChange={onOpenChange}><SheetContent><SheetTitle className="sr-only">Lead details</SheetTitle><SheetDescription className="sr-only">Loading</SheetDescription></SheetContent></Sheet>;

  const meta = statusMeta(lead.status);
  const SourceIcon = sourceIcon(lead.source);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-card border-border w-full sm:max-w-xl overflow-y-auto p-0" data-testid="lead-drawer">
        <SheetTitle className="sr-only">Lead details for {lead.name}</SheetTitle>
        <SheetDescription className="sr-only">Lead profile, activity timeline, calling, SMS and follow-up scheduling.</SheetDescription>
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-lg font-bold uppercase">
                {lead.name?.slice(0, 2)}
              </div>
              <div>
                <h2 className="font-display text-xl font-bold" data-testid="drawer-lead-name">{lead.name}</h2>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                  <SourceIcon className="h-3.5 w-3.5" />
                  <span className="capitalize">{lead.source}</span>
                  {lead.company && <span>· {lead.company}</span>}
                </div>
              </div>
            </div>
            <Button size="icon" variant="ghost" onClick={remove} data-testid="delete-lead-button">
              <Trash2 className="h-4 w-4 text-rose-400" />
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-5">
            <div>
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={lead.status} onValueChange={changeStatus}>
                <SelectTrigger className="mt-1" data-testid="drawer-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {STATUSES.map((s) => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Deal value</Label>
              <p className="text-lg font-bold mt-2 text-emerald-400">{fmtMoney(lead.deal_value)}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-4 text-sm">
            {lead.phone && <span className="flex items-center gap-1.5 text-muted-foreground"><Phone className="h-3.5 w-3.5" /> {lead.phone}</span>}
            {lead.social_handle && <span className="text-pink-400">{lead.social_handle}</span>}
            {lead.website && <a href={lead.website.startsWith("http") ? lead.website : `https://${lead.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-primary hover:underline" data-testid="lead-website-link"><Globe className="h-3.5 w-3.5" /> Website</a>}
          </div>
          {lead.address && <p className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground"><MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {lead.address}</p>}
          <LeadEmail lead={lead} onChange={refresh} />
          <Button size="sm" variant="outline" className="mt-3" onClick={enrichLead} disabled={enriching} data-testid="enrich-lead-button">
            <MapPin className="h-3.5 w-3.5 mr-1.5" /> {enriching ? "Enriching…" : "Enrich from Google"}
          </Button>
          <div className="mt-4">
            <Label className="text-xs text-muted-foreground">Assigned rep</Label>
            <Select value={lead.assigned_to || "unassigned"} onValueChange={(v) => assign(v === "unassigned" ? "" : v)}>
              <SelectTrigger className="mt-1" data-testid="assign-rep-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {team.map((m) => (
                  <SelectItem key={m.id} value={m.email}>{m.name} ({m.role})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Tabs defaultValue={defaultTab} className="p-6">
          <TabsList className="grid grid-cols-4 bg-secondary">
            <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
            <TabsTrigger value="call" data-testid="tab-call">Call</TabsTrigger>
            <TabsTrigger value="sms" data-testid="tab-sms">SMS</TabsTrigger>
            <TabsTrigger value="followup" data-testid="tab-followup">Follow-up</TabsTrigger>
          </TabsList>

          <TabsContent value="timeline" className="mt-5 space-y-4">
            <div className="space-y-2">
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note..." rows={2} data-testid="note-input" />
              <Button size="sm" onClick={addNote} variant="outline" data-testid="add-note-button">Add note</Button>
            </div>
            <div className="space-y-3" data-testid="activity-timeline">
              {activities.length === 0 && <p className="text-sm text-muted-foreground">No activity yet.</p>}
              {activities.map((a) => {
                const Icon = activityIcon[a.type] || StickyNote;
                return (
                  <div key={a.id} className="flex gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 border-b border-border pb-3">
                      <p className="text-sm">{a.body}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{fmtDate(a.created_at)} · {a.created_by}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="call" className="mt-5 space-y-3">
            <Button onClick={aiCall} className="w-full bg-primary/10 text-primary hover:bg-primary/20 border border-primary/30" data-testid="ai-call-button">
              🤖 Queue AI to qualify this lead by phone
            </Button>
            <ClickToCall lead={lead} onLogged={refresh} />
          </TabsContent>

          <TabsContent value="sms" className="mt-5 space-y-4">
            <div className="space-y-2 max-h-64 overflow-y-auto rounded-lg bg-background/50 p-3" data-testid="sms-thread">
              {messages.length === 0 && <p className="text-sm text-muted-foreground">No messages yet.</p>}
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.direction === "inbound" ? "justify-start" : "justify-end"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.direction === "inbound" ? "bg-secondary" : "bg-primary text-primary-foreground"}`}>
                    {m.body}
                    <div className={`text-[10px] mt-1 ${m.direction === "inbound" ? "text-muted-foreground" : "text-primary-foreground/70"}`}>
                      {fmtDate(m.created_at)} {m.meta?.status ? `· ${m.meta.status}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {templates.map((t) => (
                <button key={t.id} onClick={() => setSms(t.body)} className="text-xs px-2 py-1 rounded-md bg-secondary hover:bg-secondary/70 text-muted-foreground" data-testid={`template-${t.id}`}>
                  {t.title}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={sms} onChange={(e) => setSms(e.target.value)} placeholder="Type a message ({name} = lead name)" data-testid="sms-input" />
              <Button onClick={sendSms} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="send-sms-button">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="followup" className="mt-5 space-y-4" data-testid="follow-up-scheduler-form">
            <div className="space-y-2">
              <Label>Schedule automated SMS follow-up</Label>
              <Input type="datetime-local" value={fuDate} onChange={(e) => setFuDate(e.target.value)} data-testid="followup-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Message (optional)</Label>
              <Textarea value={fuMsg} onChange={(e) => setFuMsg(e.target.value)} rows={3} placeholder="Leave blank for default follow-up. Use {name}." data-testid="followup-message-input" />
            </div>
            <Button onClick={scheduleFollowup} className="bg-primary text-primary-foreground hover:bg-primary/90 w-full" data-testid="schedule-followup-button">
              <CalendarClock className="h-4 w-4 mr-2" /> Schedule follow-up
            </Button>
            <p className="text-xs text-muted-foreground">Automated follow-ups are sent by the system every 15 minutes once due.</p>

            <div className="pt-4 border-t border-border space-y-3">
              <Label className="flex items-center gap-2"><Repeat className="h-4 w-4 text-primary" /> Drip sequences</Label>
              {enrollments.filter((e) => e.status === "active").length > 0 && (
                <div className="space-y-2" data-testid="active-enrollments">
                  {enrollments.filter((e) => e.status === "active").map((e) => (
                    <div key={e.id} className="flex items-center justify-between rounded-lg bg-secondary/50 px-3 py-2 text-sm">
                      <span>{e.sequence_name} <span className="text-xs text-muted-foreground">· step {(e.current_step || 0) + 1}</span></span>
                      <Button size="sm" variant="ghost" className="h-7 text-rose-400" onClick={() => stopEnroll(e.id)} data-testid={`stop-enrollment-${e.id}`}>Stop</Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {sequences.filter((s) => s.active).map((s) => {
                  const enrolled = enrollments.some((e) => e.sequence_id === s.id && e.status === "active");
                  return (
                    <Button key={s.id} size="sm" variant="outline" disabled={enrolled} onClick={() => enroll(s.id)} data-testid={`enroll-${s.id}`}>
                      <Repeat className="h-3.5 w-3.5 mr-1.5" /> {enrolled ? `In ${s.name}` : `Enroll: ${s.name}`}
                    </Button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">Drips auto-stop when the lead replies or is marked Won.</p>
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
